
import Vue, { VueConstructor } from 'vue';
import { debounce } from '@owc-utils/Events';
import MediaGalleryCarouselItemComponent from './MediaGalleryCarouselItem.vue';
import MediaGalleryCarouselThumbnails from './MediaGalleryCarouselThumbnails.vue';
import {
  MediaGalleryOverlayData,
  MediaGalleryOverlayRefs,
  MediaGalleryOverlayComputed,
  MediaGalleryOverlayProps,
  MediaGalleryOverlayMethods,
  MediaGalleryCarouselVideo,
  MediaGalleryCarouselItem,
} from './MediaGalleryOverlay';
import { ScrollLockService } from '@workbench/core';
import { MediaQuery } from '@owc-utils/MediaQueryService/MediaQuery';
import { MediaQueryChangeEvent } from '@owc-utils/MediaQueryService/MediaQueryChangeEvent';
import { createEventListener } from '@owc-utils/Mixins/EventListenerMixin';
import {
  isCarouselItemAVideo,
  transformCarouselToUnifiedFormat,
  resizeObserverCallback,
} from './MediaGalleryOverlayUtils';
import { MediaQueryService, WB_RESIZE_EVENT_NAME, Breakpoints } from '@owc-utils/MediaQueryService';
import {
  MEDIA_OVERLAY_THROTTLE_LIMIT,
  MEDIA_OVERLAY_CLOSE_EVENT_NAME,
  THUMBNAILS_FORCED_IMAGE_RATIO,
} from './MediaGalleryOverlay.config';
import Overlay from '@/packages/Overlay/src/Overlay.vue';
// Since there's no official workbench icon for this one, we had to import it manually.
// The designers put the icon on the specs so we could download it and use it.
// https://jira.mercedes-benz.io/browse/CINOWF-29584
import OverlayPinchIcon from '!!raw-loader!./MediaGalleryPinchIcon.svg';
import { MEDIA_GALLERY_DEBOUNCE_TIME } from '../mediaGallery.config';
import { CUSTOM_EVENTS as VIDEO_EVENTS } from '@owc-packages/VideoPlayer/src/VideoPlayer.config';

const DESKTOP_VIEWPORT = [Breakpoints.mq4, Breakpoints.mq5, Breakpoints.mq6];
const scrollLockService = new ScrollLockService();
const mediaQueryService = new MediaQueryService(window);

export default (Vue as VueConstructor<Vue & { $refs: MediaGalleryOverlayRefs }>).extend<
  MediaGalleryOverlayData,
  MediaGalleryOverlayMethods,
  MediaGalleryOverlayComputed,
  MediaGalleryOverlayProps
>({
  name: 'owc-media-gallery-overlay',
  mixins: [
    createEventListener('resize', 'checkContentOverlow', {
      throttleLimit: MEDIA_OVERLAY_THROTTLE_LIMIT,
    }),
  ],
  props: {
    isDarkMode: {
      type: Boolean,
      default: false,
    },
    galleryItems: {
      type: Array,
    },
    overlayPinchLabel: {
      type: String,
      required: true,
    },
    galleryItemIndex: {
      type: Number,
      default: 0,
    },
  },
  components: {
    MediaGalleryCarouselItemComponent,
    MediaGalleryCarouselThumbnails,
    Overlay,
  },
  data: () => ({
    closeButtonClass: '',
    roundButtonClass: '',
    isContentOverflowing: false,
    currentSlide: 0,
    isMaximized: false,
    isOverlayClosed: true,
    ratio: THUMBNAILS_FORCED_IMAGE_RATIO,
    OverlayPinchIcon,
    isCurrentVideoPlaying: false,
    currentVideoPlayButton: null,
    showCloseButton: true,
  }),
  computed: {
    currentItem() {
      return this.galleryItems[this.currentSlide];
    },
    maximizedSrc() {
      let src;
      if (this.currentItem && !isCarouselItemAVideo(this.currentItem)) {
        const item = transformCarouselToUnifiedFormat(this.currentItem);
        src = item.src;
      }
      return src;
    },
    isNavigationDotsSticky() {
      return !this.isContentOverflowing;
    },
    isFirstSlide() {
      return this.currentSlide === 0;
    },
    isLastSlide() {
      return this.galleryItems.length - 1 === this.currentSlide;
    },
    isScreenMaxVisible() {
      return !isCarouselItemAVideo(this.currentItem);
    },
  },
  watch: {
    isCurrentVideoPlaying(newValue) {
      const currentVideoItem = this.currentItem as MediaGalleryCarouselItem<MediaGalleryCarouselVideo>;
      // hide the button
      this.showCloseButton = !(
        newValue && // if video is playing
        // and it opens in a modal
        currentVideoItem.json.videoDetails.openInModal
      );
    },
    isMaximized(newValue) {
      const mediaGalleryOverlay = this.$refs.mediaGalleryOverlay;
      let left = 0;
      if (newValue) {
        const slide = this.$refs.overlayMaximize;
        const newWidth = (window.innerHeight / 9) * 16;
        slide.style.setProperty('width', `${newWidth}px`);
        const scrollPosition = newWidth / 2 - window.innerWidth / 2;
        left = scrollPosition;
      }
      this.$nextTick(() => {
        mediaGalleryOverlay.scrollTo(left, 0);
      });
    },
  },
  mounted() {
    this.currentSlide = this.galleryItemIndex;

    const overlaySlider = this.$refs.overlaySlider;
    const resizeObserver = new ResizeObserver(
      debounce(MEDIA_GALLERY_DEBOUNCE_TIME, resizeObserverCallback, false),
    );

    resizeObserver.observe(overlaySlider);
    window.addEventListener(WB_RESIZE_EVENT_NAME, this.resizeHandler as EventListener);
    mediaQueryService.observe(true);

    const currentMediaQuery = mediaQueryService.getCurrentMediaQuery();
    this.updateCloseButton(currentMediaQuery);
    this.checkContentOverlow();
    scrollLockService.lock();
    this.bindKeyboardKeys();

    this.$el.addEventListener(VIDEO_EVENTS.start, () => {
      this.isCurrentVideoPlaying = true;
    });
    this.$el.addEventListener(VIDEO_EVENTS.stop, () => {
      this.isCurrentVideoPlaying = false;
    });
  },
  beforeDestroy(): void {
    window.removeEventListener(WB_RESIZE_EVENT_NAME, this.resizeHandler as EventListener);
    scrollLockService.unlock();
    this.unbindKeyboardKeys();
  },
  methods: {
    stopVideo() {
      if (this.isCurrentVideoPlaying) {
        this.isCurrentVideoPlaying = false;
        this.clickPlayButton();
      }
    },
    clickPlayButton() {
      this.currentVideoPlayButton?.click();
    },
    setCurentVideoPlayer() {
      const content = this.$refs.overlayContent;
      const current = content?.querySelector('.owc-media-gallery-overlay__current');
      const playButton = current?.querySelector(
        '.owc-video-player__play-button, [data-plyr="play"]',
      ) as HTMLButtonElement;
      if (playButton) {
        this.currentVideoPlayButton = playButton;
      }
    },
    resizeHandler(resizeEvent: CustomEvent<MediaQueryChangeEvent>) {
      this.updateCloseButton(resizeEvent.detail.current);
    },
    updateCloseButton(mediaQuery: MediaQuery) {
      if (DESKTOP_VIEWPORT.includes(mediaQuery)) {
        this.roundButtonClass = '';
      } else {
        this.roundButtonClass = 'wb-round-button--solid-level-0';
      }
    },
    /**
     * Is checking if text element collides with the navigation dots
     * if this is the case `isContentOverflowing` is set to true.
     * Reason:
     * Navigation dots need to stay to the bottom and this is fine
     * for the most cases. In a case with a long title and descriptions
     * the content becomes scrollable and it doesn't look good with the
     * navigations dots drawn over the text. In this case it should stick
     * to the bottom of the text elements and not to the bottom of the screen.
     */
    checkContentOverlow(): void {
      const content = this.$refs.overlayContent;
      const navigationDots = this.$refs.overlayNavigationDots;

      if (content && navigationDots) {
        const contentRect = content.getBoundingClientRect();
        const navigationDotsRect = navigationDots.getBoundingClientRect();

        // checking if the bottom corner intersects the the navigation dots.
        this.isContentOverflowing =
          contentRect.bottom >= navigationDotsRect.top && content.clientHeight >= window.innerHeight;
      }
    },
    bindKeyboardKeys() {
      document.addEventListener('keydown', this.handleKeyboardKeys);
    },
    unbindKeyboardKeys() {
      document.removeEventListener('keydown', this.handleKeyboardKeys);
    },
    handleKeyboardKeys(keyboardEvent: KeyboardEvent) {
      switch (keyboardEvent.key) {
        case 'Escape':
          this.onCloseClick();
          break;
        case 'ArrowLeft':
          this.prevSlide();
          break;
        case 'ArrowRight':
          this.nextSlide();
          break;
        default:
          return;
      }
    },
    onBackClick() {
      this.isMaximized = false;
    },
    onCloseClick() {
      this.unbindKeyboardKeys();
      this.isMaximized = false;
      this.$emit(MEDIA_OVERLAY_CLOSE_EVENT_NAME);
    },
    toggleMaximize() {
      this.isMaximized = !this.isMaximized;
      this.isOverlayClosed = false;
    },
    overlayCloseHandler() {
      this.isOverlayClosed = true;
    },
    nextSlide() {
      if (this.$refs.overlaySlider) {
        this.$refs.overlaySlider.next();
      }
    },
    prevSlide() {
      if (this.$refs.overlaySlider) {
        this.$refs.overlaySlider.previous();
      }
    },
    onDotNavigationChanged(wbchangeEvent: CustomEvent) {
      const dotNavigationPositon = wbchangeEvent.detail - 1;
      if (dotNavigationPositon < this.currentSlide) {
        this.prevSlide();
      } else {
        this.nextSlide();
      }
      this.currentSlide = dotNavigationPositon;
    },
    carouselSlideChanged(wbchangeEvent: CustomEvent) {
      const carouselPosition = wbchangeEvent.detail - 1;
      if (carouselPosition !== this.currentSlide) {
        this.currentSlide = carouselPosition;
      }
      this.setCurentVideoPlayer();
      this.stopVideo();
    },
    updateCurrentItem(targetSlide: number) {
      this.$refs.overlaySlider.scrollToItem(targetSlide);
    },
  },
});
